import { type ReactNode } from 'react'
import {
  ScreenHeaderProvider,
  ScreenHeaderBorder,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
} from '@/components/ScreenHeader'
import { IconButton } from '@/components/IconButton'
import { links } from '@/utils/link'
import { AccountSubviewScreenHeaderContent } from 'features/account/components/AccountSubviewScreenHeaderContent'
import { Button } from '@/components/Button'
import { AccountSubviewMainContent } from 'features/account/components/AccountSubviewMainContent'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { useIsSpacious } from '@/hooks/viewport-hooks'

interface PaymentsViewProps {
  children?: ReactNode
}

export function PaymentsView({ children }: PaymentsViewProps) {
  const isSpacious = useIsSpacious()
  return (
    <>
      {!isSpacious && (
        <ScreenHeaderProvider
          fadeVariant="aggressive"
          applyDocumentScrollPadding
        >
          <ScreenHeaderPersistentTopBar>
            <ScreenHeaderPersistentTopBarLayout
              left={
                <IconButton name="back" label="Back" to={links.account()} />
              }
            >
              <ScreenHeaderPersistentTopBarTitle>
                Payment details
              </ScreenHeaderPersistentTopBarTitle>
            </ScreenHeaderPersistentTopBarLayout>
          </ScreenHeaderPersistentTopBar>
          <ScreenHeaderMain>
            <AccountSubviewScreenHeaderContent
              kicker="Account"
              title="Payment details"
            />
            <ScreenHeaderBorder />
          </ScreenHeaderMain>
        </ScreenHeaderProvider>
      )}
      <AccountSubviewMainContent>{children}</AccountSubviewMainContent>
    </>
  )
}

interface PaymentsViewSection {
  children?: ReactNode
}

export function PaymentsViewSection({ children }: PaymentsViewSection) {
  return <div>{children}</div>
}

interface BillingMethodsEmptyProps {
  onAction?: () => unknown
  addButtonTestId?: string
}

export function BillingMethodsEmpty({
  onAction,
  addButtonTestId,
}: BillingMethodsEmptyProps) {
  return (
    <EmptyState>
      <EmptyStateIconDecoration icon="card" />
      <EmptyStateTitle>No payment methods added</EmptyStateTitle>
      <EmptyStateDescription>
        Add a card below to make checking out even faster.
      </EmptyStateDescription>
      <EmptyStateActions>
        <Button
          iconLeft="add"
          label="Add payment method"
          onPress={onAction}
          testId={addButtonTestId}
        />
      </EmptyStateActions>
    </EmptyState>
  )
}

interface MethodAddedDialogProps {
  isOpen: boolean
  workspaceName: string
  onClose: () => void
  testId?: string
  confirmButtonTestId?: string
}

export function MethodAddedDialog({
  isOpen,
  workspaceName,
  onClose,
  testId,
  confirmButtonTestId,
}: MethodAddedDialogProps) {
  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>New payment method added</ModalDialogTitle>
        <ModalDialogDescription>
          You can now use this method to make payments on your {workspaceName}{' '}
          account.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Got it"
            onPress={onClose}
            testId={confirmButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}
